/**
 * Cross-app sign-in/sign-out signalling through one shared cookie.
 *
 * ── The problem ───────────────────────────────────────────────────────────────────────────────
 *
 * Cortex and CoordioCore are separate origins, and both keep their tokens in `sessionStorage` —
 * per tab AND per origin. So signing out of Core clears Core's token and B2C's cookie, but this
 * app's tab still holds a perfectly valid access token and carries on working until that token
 * expires. B2C revokes its own session cookie on sign-out; it does not invalidate access tokens it
 * has already handed out. On a shared workstation that means the previous user's data stays on
 * screen after they believe they have signed out.
 *
 * ── Why a cookie, and not B2C's own front-channel logout ──────────────────────────────────────
 *
 * B2C can call each app's registered logout URL, but it does so through hidden iframes — a
 * third-party context whose cookies modern browsers block. It also only reaches apps whose tokens
 * live in `localStorage`, because an iframe cannot see another tab's `sessionStorage`, and moving
 * tokens to `localStorage` would persist them to disk.
 *
 * This cookie is written and read by each app's own JavaScript on its own top-level document, so
 * there is no embedded context for a browser policy to block. Cookies also ignore the port, which
 * is what makes it work across `localhost:4400` and `localhost:5173` in development, and across
 * `*.coordio-dev.health` once deployed — with no infrastructure change.
 *
 * ── Kept byte-identical with CoordioCore ──────────────────────────────────────────────────────
 *
 * This is a deliberate copy of `CoordioCore/frontend/libs/shell/src/session-epoch.ts` — Cortex is a
 * separate repository and cannot import it. **The cookie name, the value format and the domain
 * rule must stay identical in both**, because each app has to read what the other one wrote. A
 * rename on one side does not fail loudly; it just silently stops signing the other app out, which
 * is the exact failure this exists to prevent. Change one, change both.
 *
 * ── What is stored ────────────────────────────────────────────────────────────────────────────
 *
 * Two millisecond timestamps. No token, no user id, no tenant — so the cookie is not a credential
 * and needs no protection beyond staying same-site.
 */

const COOKIE_NAME = 'coordio.session-epoch';

/**
 * How long the signal must outlive a sign-out: long enough that a tab left open over a lunch break
 * still sees it, short enough not to be a permanent record. Refreshed on every sign-in and sign-out.
 */
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24;

/** The shared state: when the last sign-in happened, and when the last sign-out happened. */
export interface SessionEpoch {
  /** Epoch ms of the most recent sign-in by any app, or 0 when unknown. */
  readonly signedInAt: number;
  /** Epoch ms of the most recent sign-out by any app, or 0 when unknown. */
  readonly signedOutAt: number;
}

const EMPTY_EPOCH: SessionEpoch = { signedInAt: 0, signedOutAt: 0 };

/**
 * Cookie `Domain` so every product app shares one jar.
 *
 * Omitted on a bare host (`localhost`, or an IP): browsers reject a `Domain` on a single-label
 * host, and it is unnecessary there because cookies ignore the port. On a real host the registrable
 * domain is used, so `core.coordio-dev.health` and `cortex.coordio-dev.health` both see it.
 */
function cookieDomainAttribute(): string {
  if (typeof window === 'undefined') return '';
  const { hostname } = window.location;
  if (hostname === 'localhost' || /^[\d.]+$/.test(hostname) || !hostname.includes('.')) {
    return '';
  }
  const labels = hostname.split('.');
  const registrable = labels.slice(-2).join('.');
  return `; Domain=.${registrable}`;
}

function readCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(`${COOKIE_NAME}=`));
  return match ? decodeURIComponent(match.slice(COOKIE_NAME.length + 1)) : null;
}

function writeCookie(epoch: SessionEpoch): void {
  if (typeof document === 'undefined') return;
  const value = encodeURIComponent(`${epoch.signedInAt}.${epoch.signedOutAt}`);
  const secure = window?.location.protocol === 'https:' ? '; Secure' : '';
  // SameSite=Lax is enough: only our own top-level documents ever read it, it is never sent from a
  // cross-site context, and it carries no credential.
  document.cookie =
    `${COOKIE_NAME}=${value}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax` +
    `${secure}${cookieDomainAttribute()}`;
}

/** Current shared state. Returns zeroes when the cookie is absent or malformed. */
export function readSessionEpoch(): SessionEpoch {
  const raw = readCookie();
  if (!raw) return EMPTY_EPOCH;
  const [signedInAt, signedOutAt] = raw.split('.').map((part) => Number.parseInt(part, 10));
  if (!Number.isFinite(signedInAt) || !Number.isFinite(signedOutAt)) return EMPTY_EPOCH;
  return { signedInAt: signedInAt || 0, signedOutAt: signedOutAt || 0 };
}

/**
 * Announces a sign-out to every other app.
 *
 * Must be called BEFORE handing the browser to B2C's logout endpoint: that redirect ends this
 * document, so anything queued after it never runs and the other apps never hear about it.
 */
export function markSignedOut(): void {
  const current = readSessionEpoch();
  writeCookie({ signedInAt: current.signedInAt, signedOutAt: Date.now() });
}

/** Announces a sign-in, so tabs sitting on a login screen know a session exists again. */
export function markSignedIn(): void {
  const current = readSessionEpoch();
  writeCookie({ signedInAt: Date.now(), signedOutAt: current.signedOutAt });
}

/**
 * Whether a token obtained at `tokenAcquiredAt` has been invalidated by a sign-out elsewhere.
 *
 * A sign-out only counts when it happened AFTER this tab got its token: one that predates it
 * belongs to a previous session and must not log the user straight back out of a fresh sign-in.
 */
export function isSignedOutElsewhere(tokenAcquiredAt: number): boolean {
  const { signedOutAt } = readSessionEpoch();
  return signedOutAt > 0 && tokenAcquiredAt > 0 && signedOutAt > tokenAcquiredAt;
}
